'use client';

import ShopCartBottom from '@/components/shop/shop-cart-bottom';
import ShopCartItem from '@/components/shop/shop-cart-item';
import { fetchShopDetailOption } from '@/lib/api/fetch-shop-detail-option';
import { fetchShopOptionsCounting } from '@/lib/api/fetch-shop-options-counting';
import { fetchShopOptionsCountingVisible } from '@/lib/api/fetch-shop-options-counting-visible';
import useLogEvents from '@/lib/client-hook/use-log-events';
import { useDictionary } from '@/lib/context/locale-provider';
import { Languages } from '@/lib/dictionaries';
import { RootState } from '@/lib/store';
import { setErrorMessage } from '@/slice/error';
import {
  addCart,
  allDeleteCart,
  deleteCart,
  removeFromCart,
  setShopId,
  setShopTitle,
} from '@/slice/shopCartSlice';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import classes from './shop-cart.module.scss';
import 'react-tooltip/dist/react-tooltip.css';

const initSelectOption = {
  values: [],
  titles: [],
};

function ShopCart({
  lang,
  shopId,
  title,
  initialOptions,
  isMobile,
  isEnded,
  pricingMode,
  dict: { selectOption, placeholderOption },
}: {
  lang: Languages;
  shopId: string;
  title: { ko: string; en: string; ja: string; id: string; zh: string };
  initialOptions: any;
  isMobile: boolean;
  isEnded: boolean;
  pricingMode: string;
  dict: {
    selectOption: string;
    placeholderOption: string;
  };
}) {
  const dict = useDictionary();
  const dispatch = useDispatch();

  const cartItems = useSelector((state: RootState) => state.shopCart.items);

  const { logShareEvent } = useLogEvents();

  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  const placeholderSelect = placeholderOption;
  const selectOptionRef = useRef<{ values: string[]; titles: string[] }>(
    initSelectOption,
  );
  const [optionSelectPlaceholder, setOptionSelectPlaceholder] = useState<
    string[]
  >([placeholderSelect]);
  const [openOptions, setOpenOptions] = useState<boolean[]>([]);
  const [options, setOptions] = useState<any[]>(initialOptions);
  const [optionStockVisible, setOptionStockVisible] = useState<boolean>(false);
  const [stock, setStock] = useState<{
    [key: string]: { useStock: boolean; stock: number; soldCount: number };
  }>({});

  useEffect(() => {
    const fetchShopData = async () => {
      dispatch(setShopId(shopId));
      dispatch(setShopTitle(title));
      dispatch(allDeleteCart());

      const [shopOption, optionStockVisibleData, optionCountingInfo] =
        await Promise.all([
          fetchShopDetailOption(shopId, lang),
          fetchShopOptionsCountingVisible(shopId),
          fetchShopOptionsCounting(shopId),
        ]);

      setOptions(shopOption);
      setOptionStockVisible(optionStockVisibleData.optionStockVisible);
      setStock(optionCountingInfo);
    };

    fetchShopData();
  }, [shopId, lang, dispatch, title]);

  const handleCloseCart = () => {
    if (isMobile && mobileCartOpen) {
      setMobileCartOpen(false);
    }
  };

  const handleCartAction = useCallback(
    (action: 'add' | 'remove' | 'delete', item: any) => {
      if (action === 'add') {
        const itemStock = stock[item.serial];
        if (
          itemStock.useStock &&
          itemStock.stock - itemStock.soldCount - item.count === 0
        ) {
          dispatch(setErrorMessage(dict!.shop.error.insufficientStock));
          return;
        }
        dispatch(addCart(item));
      } else if (action === 'remove') {
        dispatch(removeFromCart(item));
      } else {
        dispatch(deleteCart(item));
      }
    },
    [stock, dispatch, dict],
  );

  const handleOptionSelectPlaceholder = useCallback(
    (index: number, message: string) => {
      setOptionSelectPlaceholder(prev => {
        const updated = [...prev];
        updated[index] = message;
        return updated;
      });
    },
    [],
  );

  const handleToggleOpenOption = useCallback((index: number) => {
    setOpenOptions(prev => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  }, []);

  const handleAddOption = useCallback(
    (
      title?: any,
      options?: any[],
      index?: number,
      selectOptionName?: string,
      selectOptionPrice?: number,
      selectOptionWeight?: number,
      selectOptionSerial?: string,
    ) => {
      logShareEvent('bnb_shop_detail_option', {});

      if (index! === 0 && selectOptionRef.current.values[index!] !== '') {
        setOptions(initialOptions);
      }

      if (selectOptionName != null) {
        selectOptionRef.current.titles[index!] = selectOptionName;
        handleToggleOpenOption(index!);
        handleOptionSelectPlaceholder(index!, selectOptionName!);
      }

      if (title && options) {
        setOptions(prev => [
          ...prev,
          {
            title: title,
            options: options,
          },
        ]);
        handleOptionSelectPlaceholder(index! + 1, placeholderSelect);
      } else {
        // 25.02.21 옵션을 선택해도 리셋 안되도록 수정 (유저 피드백)
        // setOptionSelectPlaceholder([placeholderSelect]);
        setOpenOptions([false]);
        // setOptions(initialOptions);

        dispatch(
          addCart({
            serial: selectOptionSerial as string,
            title: selectOptionRef.current.titles.join(' / '),
            price: selectOptionPrice as number,
            count: 1,
            weight: selectOptionWeight as number,
          }),
        );

        selectOptionRef.current = initSelectOption;
      }
    },
    [
      dispatch,
      handleOptionSelectPlaceholder,
      handleToggleOpenOption,
      initialOptions,
      logShareEvent,
      placeholderSelect,
    ],
  );

  return (
    <article className={classes.detailFloatingView}>
      <div className={classes.detailFloating}>
        <div className={classes.floatingWrap}>
          <div
            className={classes.floatingGroupList}
            style={{
              display: isMobile ? (mobileCartOpen ? 'block' : 'none') : 'block',
            }}
          >
            <div className={classes.floatingOptionBox}>
              <div className={classes.labelBox}>{selectOption}</div>
              <div className={classes.optionItemList}>
                {options.length > 0 &&
                  options.map((option, index) => (
                    <div
                      className={classes.optionItemBox}
                      key={`option-${index}`}
                    >
                      <div className={classes.optionLabel}>
                        {option.title[lang]}
                      </div>
                      <div className={classes.optionSelector}>
                        <a
                          className={classes.optionSetBtn}
                          onClick={() => handleToggleOpenOption(index)}
                        >
                          <span className={classes.setTxt}>
                            {optionSelectPlaceholder[index]}
                          </span>
                        </a>
                        <ul
                          style={{
                            display: openOptions[index] ? 'block' : 'none',
                          }}
                        >
                          {option.options &&
                            option.options.length > 0 &&
                            option.options.map(
                              (subOption: any, subIndex: number) => {
                                const remaining =
                                  subOption.serial && subOption.useStock
                                    ? subOption.stock -
                                      stock[subOption.serial].soldCount
                                    : 1;
                                return (
                                  <li key={`subOption-${subIndex}`}>
                                    <button
                                      disabled={remaining === 0}
                                      onClick={() =>
                                        handleAddOption(
                                          subOption.title,
                                          subOption.options,
                                          index,
                                          subOption.name[lang],
                                          pricingMode === 'dynamic'
                                            ? subOption.price
                                            : subOption.pricesByCurrency
                                              ? subOption.pricesByCurrency[
                                                  lang === 'ko'
                                                    ? 'KRW'
                                                    : lang === 'ja'
                                                      ? 'JPY'
                                                      : 'USD'
                                                ]
                                              : undefined,
                                          subOption.weight,
                                          subOption.serial,
                                        )
                                      }
                                    >
                                      <div>
                                        <span
                                          className={
                                            remaining > 0
                                              ? classes.title
                                              : classes.titleCancelLine
                                          }
                                        >
                                          {subOption.name[lang]}&nbsp;
                                        </span>
                                        {subOption.useStock &&
                                          (remaining === 0 ? (
                                            <span className={classes.soldOut}>
                                              ({dict!.common.soldOut})
                                            </span>
                                          ) : optionStockVisible ? (
                                            <span className={classes.remaining}>
                                              (
                                              {dict!.shop.remaining.replace(
                                                '{n}',
                                                remaining,
                                              )}
                                              )
                                            </span>
                                          ) : (
                                            ''
                                          ))}
                                      </div>
                                    </button>
                                  </li>
                                );
                              },
                            )}
                        </ul>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className={classes.floatingResultBox}>
              <div className={classes.resultList}>
                {cartItems.length > 0 &&
                  cartItems.map((item, index) => (
                    <ShopCartItem
                      key={`cart-item-${index}`}
                      title={item.title}
                      price={item.price}
                      count={item.count}
                      currency={dict!.common.currency}
                      onClickMinus={() => handleCartAction('remove', item)}
                      onClickPlus={() => handleCartAction('add', item)}
                      onClickDelete={() => handleCartAction('delete', item)}
                    />
                  ))}
              </div>
            </div>
          </div>
          <button
            className={classes.floatingCloseBtn}
            style={{ display: mobileCartOpen ? 'block' : 'none' }}
            onClick={handleCloseCart}
          ></button>
        </div>
        <ShopCartBottom
          lang={lang}
          isEnded={isEnded}
          onMobileShow={() => {
            if (isMobile) {
              setMobileCartOpen(true);
            }
          }}
        />
      </div>
    </article>
  );
}

export default ShopCart;
